import { useEffect, useMemo, useState } from "react";
import type { PhoneNumberDTO, PhoneNumbersSummary } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useConnectionContext } from "@/lib/connectionContext";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";
import { SummaryCards, type SummaryCard } from "@/components/SummaryCards";
import { CopyableId } from "@/components/ui/CopyableId";
import { ExternalLinkIcon } from "@/lib/icons";

type SummaryState =
  | { status: "loading" }
  | { status: "ready"; data: PhoneNumbersSummary }
  | { status: "error"; message: string };

const PAGE_SIZE = 25;

type CapFilter = "all" | "voice" | "sms" | "mms";

type State =
  | { status: "loading" }
  | { status: "ready"; rows: PhoneNumberDTO[] }
  | { status: "error"; message: string };

/**
 * Phone-numbers management view. Inventory of every IncomingPhoneNumber
 * synced from the user's Twilio accounts. Read-only at MVP — actual
 * provisioning / configuration still happens in Twilio Console (we link out).
 */
export function PhoneNumbersPage() {
  const ctx = useConnectionContext();
  const scope = ctx.allSelected ? undefined : ctx.selectedIds;
  const scopeKey = scope ? scope.join(",") : "all";

  const [state, setState] = useState<State>({ status: "loading" });
  const [summary, setSummary] = useState<SummaryState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [capFilter, setCapFilter] = useState<CapFilter>("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Summary refetches only on scope change (point-in-time, no time window).
  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setSummary({ status: "loading" });
    void api.phoneNumbers
      .summary(scope)
      .then((data) => {
        if (!cancelled) setSummary({ status: "ready", data });
      })
      .catch((err) => {
        if (!cancelled) {
          setSummary({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  useEffect(() => {
    if (ctx.status !== "ready") return;
    let cancelled = false;
    setState({ status: "loading" });
    void api.phoneNumbers
      .list(scope, 1000)
      .then((res) => {
        if (!cancelled) setState({ status: "ready", rows: res.phoneNumbers });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.status, scopeKey]);

  // Reset progressive-reveal window whenever filters change so the user lands
  // at the top of the new filtered set instead of mid-scroll.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, capFilter, scopeKey]);

  // Derive filtered rows (client-side; v1 has at most ~1k numbers per tenant).
  const filtered = useMemo(() => {
    if (state.status !== "ready") return [];
    const term = search.trim().toLowerCase();
    return state.rows.filter((p) => {
      if (capFilter !== "all" && !p.capabilities[capFilter as "voice" | "sms" | "mms"]) {
        return false;
      }
      if (term.length === 0) return true;
      return (
        p.phoneNumber.toLowerCase().includes(term) ||
        (p.friendlyName ?? "").toLowerCase().includes(term)
      );
    });
  }, [state, search, capFilter]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  const hasMore = visible.length < filtered.length;

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    enabled: hasMore,
    onLoadMore: () => setVisibleCount((n) => n + PAGE_SIZE),
  });

  // Connection lookup so each row can show which account the number belongs to.
  const connNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of ctx.connections) {
      map.set(c.id, c.friendlyName ?? c.accountSid);
    }
    return map;
  }, [ctx.connections]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            Inventory
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Phone numbers
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Every IncomingPhoneNumber synced from your connected Twilio accounts.
          </p>
        </div>
        {state.status === "ready" && (
          <span className="rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-[11.5px] font-medium text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-300">
            {state.rows.length.toLocaleString("en-US")} total
          </span>
        )}
      </header>

      <SummaryCards
        loading={summary.status === "loading"}
        error={summary.status === "error" ? summary.message : null}
        cards={summary.status === "ready" ? buildPhoneNumbersSummaryCards(summary.data) : []}
      />

      <Toolbar
        search={search}
        onSearchChange={setSearch}
        capFilter={capFilter}
        onCapFilterChange={setCapFilter}
        disabled={state.status !== "ready"}
      />

      {state.status === "loading" && <SkeletonRows />}
      {state.status === "error" && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {state.message}
        </p>
      )}
      {state.status === "ready" && state.rows.length === 0 && <EmptyState />}
      {state.status === "ready" && state.rows.length > 0 && filtered.length === 0 && (
        <FilteredEmpty
          onClear={() => {
            setSearch("");
            setCapFilter("all");
          }}
        />
      )}
      {state.status === "ready" && filtered.length > 0 && (
        <>
          <Table rows={visible} connNameById={connNameById} />
          <div ref={sentinelRef} className="flex justify-center pt-2">
            {hasMore ? (
              <p className="text-[12px] text-zinc-500">
                Showing {visible.length.toLocaleString("en-US")} of{" "}
                {filtered.length.toLocaleString("en-US")} · scroll for more
              </p>
            ) : (
              <p className="text-[12px] text-zinc-500">
                {filtered.length.toLocaleString("en-US")} number
                {filtered.length === 1 ? "" : "s"} · end of results
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Toolbar({
  search,
  onSearchChange,
  capFilter,
  onCapFilterChange,
  disabled,
}: {
  search: string;
  onSearchChange: (s: string) => void;
  capFilter: CapFilter;
  onCapFilterChange: (c: CapFilter) => void;
  disabled: boolean;
}) {
  const filters: Array<{ value: CapFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "voice", label: "Voice" },
    { value: "sms", label: "SMS" },
    { value: "mms", label: "MMS" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        disabled={disabled}
        placeholder="Search by number or friendly name"
        className="h-9 min-w-[200px] max-w-md flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:bg-white focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70 dark:focus-visible:bg-zinc-950/60"
      />
      <div
        role="radiogroup"
        aria-label="Capability filter"
        className="inline-flex items-center rounded-md border border-zinc-200 bg-zinc-50 p-0.5 dark:border-zinc-800 dark:bg-zinc-900/40"
      >
        {filters.map((f) => (
          <button
            key={f.value}
            type="button"
            role="radio"
            aria-checked={capFilter === f.value}
            disabled={disabled}
            onClick={() => onCapFilterChange(f.value)}
            className={
              "rounded-[5px] px-2.5 py-1 text-[12px] font-medium transition " +
              (capFilter === f.value
                ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-50"
                : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100")
            }
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Table({
  rows,
  connNameById,
}: {
  rows: PhoneNumberDTO[];
  connNameById: Map<string, string>;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-white/[0.02]">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider dark:bg-zinc-900/60 text-zinc-500">
          <tr>
            <th className="px-4 py-2.5">Phone number</th>
            <th className="px-4 py-2.5">Capabilities</th>
            <th className="hidden px-4 py-2.5 lg:table-cell">Account</th>
            <th className="hidden px-4 py-2.5 md:table-cell">Status</th>
            <th className="px-4 py-2.5 text-right" aria-label="External link" />
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
          {rows.map((p) => {
            const connName = connNameById.get(p.connectionId) ?? "—";
            return (
              <tr key={p.id} className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60">
                <td className="px-4 py-3 align-middle">
                  <div className="font-mono text-[13.5px] text-zinc-900 dark:text-zinc-100">
                    {p.phoneNumber}
                  </div>
                  {p.friendlyName && (
                    <div className="truncate text-[11.5px] text-zinc-500">{p.friendlyName}</div>
                  )}
                  <div className="mt-0.5 flex items-center gap-1">
                    <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">
                      SID
                    </span>
                    <CopyableId value={p.id} label="phone number SID" />
                  </div>
                </td>
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-wrap gap-1">
                    <CapPill on={p.capabilities.voice} label="Voice" />
                    <CapPill on={p.capabilities.sms} label="SMS" />
                    <CapPill on={p.capabilities.mms} label="MMS" />
                    {p.capabilities.fax && <CapPill on label="Fax" />}
                  </div>
                </td>
                <td className="hidden px-4 py-3 align-middle text-[12.5px] text-zinc-600 dark:text-zinc-400 lg:table-cell">
                  {connName}
                </td>
                <td className="hidden px-4 py-3 align-middle md:table-cell">
                  <StatusPill status={p.status} />
                </td>
                <td className="px-4 py-3 align-middle text-right">
                  <a
                    href={twilioConsoleUrl(p)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open in Twilio Console"
                    aria-label={`Open ${p.phoneNumber} in Twilio Console`}
                    className="inline-flex items-center gap-1 text-[12px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                  >
                    Console
                    <ExternalLinkIcon size={12} />
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CapPill({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={
        "rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ring-1 ring-inset " +
        (on
          ? "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "bg-zinc-100 text-zinc-500 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-500 dark:ring-zinc-700")
      }
    >
      {label}
    </span>
  );
}

function StatusPill({ status }: { status: string | null }) {
  if (!status) {
    return <span className="text-[11.5px] text-zinc-500">—</span>;
  }
  const styles: Record<string, { bg: string; ring: string; text: string; dot: string }> = {
    "in-use": {
      bg: "bg-emerald-50 dark:bg-emerald-500/10",
      ring: "ring-emerald-500/30",
      text: "text-emerald-700 dark:text-emerald-300",
      dot: "bg-emerald-500 dark:bg-emerald-400",
    },
  };
  const s = styles[status] ?? {
    bg: "bg-zinc-100 dark:bg-zinc-800/60",
    ring: "ring-zinc-300 dark:ring-zinc-700",
    text: "text-zinc-700 dark:text-zinc-300",
    dot: "bg-zinc-400 dark:bg-zinc-500",
  };
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset " +
        s.bg +
        " " +
        s.ring +
        " " +
        s.text
      }
    >
      <span className={"h-1.5 w-1.5 rounded-full " + s.dot} />
      {status}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 last:border-b-0 dark:border-zinc-800/80"
        >
          <div className="space-y-2">
            <div className="h-3 w-40 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="h-2 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
          <div className="flex gap-1.5">
            <div className="h-4 w-12 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800/70" />
            <div className="h-4 w-12 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800/70" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-10 text-center">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
        No phone numbers yet
      </h2>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Phone numbers appear here within a few seconds of your first sync. If you've just connected
        an account, give it a moment and refresh.
      </p>
    </div>
  );
}

function FilteredEmpty({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900/30 p-6 text-center">
      <p className="text-[13px] text-zinc-600 dark:text-zinc-400">
        No phone numbers match the current filter.
      </p>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-[12.5px] text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
      >
        Clear filter
      </button>
    </div>
  );
}

/**
 * Build a "view in Twilio Console" URL for a given phone number row.
 * Twilio's console accepts the PN SID — opens the configuration page directly.
 */
function twilioConsoleUrl(p: PhoneNumberDTO): string {
  return `https://console.twilio.com/us1/develop/phone-numbers/manage/incoming/${encodeURIComponent(p.id)}/configure`;
}

function buildPhoneNumbersSummaryCards(s: PhoneNumbersSummary): SummaryCard[] {
  const fmt = (n: number) => Math.round(n).toLocaleString("en-US");
  const pctOf = (n: number) =>
    s.total === 0 ? "—" : `${Math.round((n / s.total) * 100)}% of inventory`;
  return [
    {
      label: "Total numbers",
      value: fmt(s.total),
      hint: s.total === 0 ? "No numbers ingested yet" : "Across all connected accounts",
    },
    {
      label: "SMS-capable",
      value: fmt(s.sms),
      hint: pctOf(s.sms),
    },
    {
      label: "Voice-capable",
      value: fmt(s.voice),
      hint: pctOf(s.voice),
    },
    {
      label: "MMS-capable",
      value: fmt(s.mms),
      hint: pctOf(s.mms),
    },
  ];
}
